/* eslint-disable */
import { AlertTriangle, Check, X } from 'lucide-react';
import BaseModal from '@/Components/Modals/BaseModal/BaseModal';
import { Button } from '@/Components/ui/button';

const valueSummary = (values = {}) => {
	const parts = [];
	if (values?.threshold !== undefined) parts.push(`Threshold: ${values.threshold}`);
	if (values?.maxStock !== undefined) parts.push(`Max Stock: ${values.maxStock}`);
	if (values?.recommendedStock !== undefined) {
		parts.push(`Recommended Stock: ${values.recommendedStock}`);
	}
	return parts.join(' • ') || 'No value';
};

/**
 * Shown after a clipboard paste when some rows could not be resolved on their
 * own: pasted names that matched more than one variant get a dropdown, names
 * that matched nothing are listed so the user knows what was skipped.
 *
 * `resolutions` is owned by the parent ({ [ambiguousIndex]: variantId }) so
 * "Apply" can be built from matched rows + the picks made here.
 */
const PasteThresholdReviewModal = (props) => {
	const {
		open,
		matchedCount,
		ambiguous,
		notFound,
		resolutions,
		onResolve,
		onApply,
		onClose,
	} = props;

	const resolvedCount = Object.values(resolutions ?? {}).filter(Boolean).length;

	return (
		<BaseModal
			open={open}
			handleClose={onClose}
			heading='Review Pasted Rows'
			width='45rem'
			height='auto'
			customStyles={{ maxHeight: '80vh' }}
			renderBtns={() => {
				return (
					<>
						<Button
							className='flex-1'
							variant='secondary'
							onClick={onClose}
						>
							Cancel
						</Button>
						<Button
							className='flex-1 primary-btn'
							onClick={onApply}
							disabled={matchedCount + resolvedCount === 0}
						>
							{`Apply ${matchedCount + resolvedCount} Row(s)`}
						</Button>
					</>
				);
			}}
		>
			<div className='flex flex-col gap-3 text-[13px]'>
				<p className='m-0 flex items-center gap-1.5 text-gray-700'>
					<Check className='h-4 w-4 text-green-600' />
					{`${matchedCount} row(s) matched and ready to apply`}
				</p>

				{ambiguous?.length > 0 && (
					<div className='flex flex-col gap-1.5'>
						<p className='m-0 flex items-center gap-1.5 font-medium text-gray-700'>
							<AlertTriangle className='h-4 w-4 text-amber-600' />
							{`${ambiguous.length} name(s) matched more than one variant — pick the right one:`}
						</p>
						<div className='flex max-h-[220px] flex-col gap-1.5 overflow-y-auto'>
							{ambiguous.map((item, index) => {
								return (
									<div
										key={index}
										className='flex flex-col gap-1 rounded-md border border-amber-100 bg-amber-50 px-3 py-2'
									>
										<div className='flex items-center justify-between gap-2'>
											<span className='truncate font-medium text-gray-800'>
												{item?.name}
											</span>
											<span className='whitespace-nowrap text-[11px] text-gray-500'>
												{valueSummary(item?.values)}
											</span>
										</div>
										<select
											value={resolutions?.[index] ?? ''}
											onChange={(e) => onResolve(index, e.target.value)}
											className='w-full rounded border border-amber-300 px-2 py-1 text-[12px] focus:border-amber-500 focus:outline-none'
										>
											<option value=''>Skip this row</option>
											{(item?.candidates || []).map((candidate) => {
												return (
													<option
														key={candidate?.variantId}
														value={candidate?.variantId ?? ''}
													>
														{candidate?.label}
													</option>
												);
											})}
										</select>
									</div>
								);
							})}
						</div>
					</div>
				)}

				{notFound?.length > 0 && (
					<div className='flex flex-col gap-1.5'>
						<p className='m-0 flex items-center gap-1.5 font-medium text-gray-700'>
							<X className='h-4 w-4 text-red-500' />
							{`${notFound.length} row(s) could not be matched in this store — they will be skipped:`}
						</p>
						<div className='flex max-h-[180px] flex-col gap-1.5 overflow-y-auto'>
							{notFound.map((item, index) => {
								return (
									<div
										key={index}
										className='flex items-center justify-between gap-2 rounded-md border border-red-100 bg-red-50 px-3 py-1.5'
									>
										<span className='truncate text-[12px] font-medium text-red-700'>
											{item?.name || '(no name)'}
											{item?.sku ? ` — ${item.sku}` : ''}
										</span>
										<span className='whitespace-nowrap text-[11px] text-red-400'>
											{valueSummary(item?.values)}
										</span>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</BaseModal>
	);
};

export default PasteThresholdReviewModal;
